import { useState } from 'react'
import PropTypes from 'prop-types'
import { Button, ListItemIcon, ListItemText, Menu, MenuItem } from '@mui/material'
import { useTranslation } from 'react-i18next'
import * as XLSX from 'xlsx'
import { FileDownloadIcon } from './Icons'

const MAX_SHEET_NAME = 31

const resolveHeader = column => column.exportLabel ?? column.headerName ?? column.field

const resolveValue = (column, row) => {
  if (column.exportValue) return column.exportValue(row)
  if (column.valueGetter) return column.valueGetter(row[column.field], row)
  return row[column.field]
}

const toCell = value => {
  if (value === null || value === undefined) return ''
  if (value instanceof Date) return value.toISOString()
  if (typeof value === 'object') return JSON.stringify(value)
  return value
}

const slugify = name =>
  String(name)
    .replace(/[^a-z0-9]+/gi, '-')
    .replace(/^-+|-+$/g, '')
    .toLowerCase() || 'export'

const download = (blob, name) => {
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = name
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

export default function ExportButton({ columns = [], rows = [], fileName = 'export', sheetName, label, ...buttonProps }) {
  const { t } = useTranslation()
  const [anchorEl, setAnchorEl] = useState(null)

  const exportCols = columns.filter(column => !column.disableExport)
  const isEmpty = !rows.length || !exportCols.length

  const close = () => setAnchorEl(null)

  const buildRows = () => rows.map(row => exportCols.map(column => toCell(resolveValue(column, row))))

  const exportCSV = () => {
    close()
    const escape = value => `"${String(value).replace(/"/g, '""')}"`
    const csv = [exportCols.map(resolveHeader), ...buildRows()].map(line => line.map(escape).join(',')).join('\n')
    download(new Blob([csv], { type: 'text/csv;charset=utf-8;' }), `${slugify(fileName)}.csv`)
  }

  const exportExcel = () => {
    close()
    const sheet = XLSX.utils.aoa_to_sheet([exportCols.map(resolveHeader), ...buildRows()])
    const book = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(book, sheet, (sheetName || fileName).slice(0, MAX_SHEET_NAME))
    XLSX.writeFile(book, `${slugify(fileName)}.xlsx`)
  }

  return (
    <>
      <Button
        size="medium"
        variant="outlined"
        startIcon={<FileDownloadIcon />}
        disabled={isEmpty}
        onClick={event => setAnchorEl(event.currentTarget)}
        {...buttonProps}
      >
        {label ?? t('Export')}
      </Button>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={close}>
        <MenuItem onClick={exportCSV}>
          <ListItemIcon>
            <FileDownloadIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('Export CSV')}</ListItemText>
        </MenuItem>
        <MenuItem onClick={exportExcel}>
          <ListItemIcon>
            <FileDownloadIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('Export Excel')}</ListItemText>
        </MenuItem>
      </Menu>
    </>
  )
}

ExportButton.propTypes = {
  columns: PropTypes.array,
  rows: PropTypes.array,
  fileName: PropTypes.string,
  sheetName: PropTypes.string,
  label: PropTypes.string,
}
